import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { PromptOverride, PromptOverrideKind } from 'gen/api';
import type { RootState } from '../store';

type PromptOverridesMap = Record<string, PromptOverride>;

interface IDeveloperToolsSliceState {
  promptOverrides: PromptOverridesMap;
  isPanelOpen: boolean;
  debugMode: boolean;
}

const STORAGE_KEY = 'developerToolsOverrides';

const overrideKey = (kind: PromptOverrideKind, path: string): string => `${kind}:${path}`;

interface IStoredDeveloperTools {
  promptOverrides?: PromptOverridesMap;
}

const loadStored = (): IStoredDeveloperTools => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as IStoredDeveloperTools;
  } catch {
    return {};
  }
};

const persist = (state: IDeveloperToolsSliceState): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ promptOverrides: state.promptOverrides }));
  } catch {
    // localStorage throws on quota or in private browsing.
  }
};

const stored = loadStored();

const initialPromptOverrides = stored.promptOverrides ?? {};

const initialState: IDeveloperToolsSliceState = {
  promptOverrides: initialPromptOverrides,
  isPanelOpen: false,
  debugMode: Object.keys(initialPromptOverrides).length > 0,
};

const developerToolsSlice = createSlice({
  name: 'developerToolsSlice',
  initialState,
  reducers: {
    setPromptOverride(state, action: PayloadAction<PromptOverride>) {
      const { kind, path, content } = action.payload;
      state.promptOverrides[overrideKey(kind, path)] = { kind, path, content };
      persist(state);
    },
    removePromptOverride(state, action: PayloadAction<{ kind: PromptOverrideKind; path: string }>) {
      delete state.promptOverrides[overrideKey(action.payload.kind, action.payload.path)];
      persist(state);
    },
    clearAllOverrides(state) {
      state.promptOverrides = {};
      persist(state);
    },
    openPanel(state) {
      state.isPanelOpen = true;
    },
    closePanel(state) {
      state.isPanelOpen = false;
    },
    setDebugMode(state, action: PayloadAction<boolean>) {
      state.debugMode = action.payload;
      persist(state);
    },
  },
});

export default developerToolsSlice.reducer;
export const developerToolsActions = developerToolsSlice.actions;

export const selectPromptOverrides = (state: RootState): PromptOverridesMap =>
  state.developerToolsSlice.promptOverrides;

export const selectPromptOverrideFiles = (state: RootState): PromptOverride[] =>
  Object.values(state.developerToolsSlice.promptOverrides);

export const selectPromptOverrideCount = (state: RootState): number =>
  Object.keys(state.developerToolsSlice.promptOverrides).length;

export const selectDebugMode = (state: RootState): boolean => state.developerToolsSlice.debugMode;
